import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Pulse, Warning, Users, Lightning, EnvelopeSimple, ChatCircle, Robot, Clock } from "@phosphor-icons/react";

export default function AdminHealth() {
  const [health, setHealth] = useState(null);
  const [failures, setFailures] = useState(null);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("health");

  useEffect(() => {
    const run = async () => {
      try {
        const [h, f, a] = await Promise.all([
          api.get("/admin/health"),
          api.get("/admin/failures"),
          api.get("/admin/audit?limit=100"),
        ]);
        setHealth(h.data);
        setFailures(f.data);
        setAudit(a.data.items || []);
      } catch (e) { /* handled below */ }
    };
    run();
  }, []);

  if (!health) {
    return (
      <div className="p-6 md:p-10" data-testid="admin-health-page">
        <h1 className="text-3xl font-bold">// system health</h1>
        <div className="mt-4 text-sm" style={{ color: "var(--text-2)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="admin-health-page">
      <h1 className="text-3xl lg:text-4xl font-bold flex items-center gap-2">
        <Pulse weight="bold" style={{ color: "var(--accent)" }} /> // system health
      </h1>
      <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Operational overview · generated {new Date(health.generated_at).toLocaleString()}</p>

      <div className="flex gap-2 mt-6">
        {["health", "failures", "audit"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="tag"
            data-testid={`tab-${t}`}
            style={{
              color: tab === t ? "var(--bg)" : "var(--text-2)",
              background: tab === t ? "var(--accent)" : "transparent",
              borderColor: tab === t ? "var(--accent)" : "var(--border)",
            }}
          >{t}</button>
        ))}
      </div>

      {tab === "health" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="health-overview">
            <Card icon={<Users weight="bold" />} label="users" value={health.users.total} sub={`${health.users.subscribed} subscribed`} testid="metric-users" />
            <Card icon={<Users weight="bold" />} label="organizations" value={health.organizations} testid="metric-orgs" />
            <Card icon={<Robot weight="bold" />} label="ai 24h" value={health.last_24h.ai_generations} testid="metric-ai" />
            <Card icon={<ChatCircle weight="bold" />} label="sms 24h" value={health.last_24h.sms_messages} testid="metric-sms" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <Card icon={<Warning weight="bold" />} label="failed campaigns" value={health.last_24h.failed_campaign_steps} testid="metric-camp-fail" danger={health.last_24h.failed_campaign_steps > 0} />
            <Card icon={<EnvelopeSimple weight="bold" />} label="failed emails" value={health.last_24h.failed_emails} testid="metric-email-fail" danger={health.last_24h.failed_emails > 0} />
            <Card icon={<ChatCircle weight="bold" />} label="failed sms" value={health.last_24h.failed_sms} testid="metric-sms-fail" danger={health.last_24h.failed_sms > 0} />
            <Card icon={<Lightning weight="bold" />} label="webhooks 24h" value={health.last_24h.webhook_events} sub={`${health.last_7d.webhook_unrouted || 0} unrouted / 7d`} testid="metric-webhooks" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            <Card icon={<Clock weight="bold" />} label="jobs pending" value={health.backlog.automation_jobs_pending} testid="metric-jobs-pending" />
            <Card icon={<Warning weight="bold" />} label="jobs failed" value={health.backlog.automation_jobs_failed} testid="metric-jobs-failed" danger={health.backlog.automation_jobs_failed > 0} />
            <Card icon={<Warning weight="bold" />} label="stuck enrollments" value={health.backlog.stuck_active_enrollments} testid="metric-stuck" danger={health.backlog.stuck_active_enrollments > 5} />
          </div>
        </>
      )}

      {tab === "failures" && failures && (
        <section className="mt-6 space-y-6" data-testid="failures-tab">
          <FailureList title="campaign step failures" items={failures.campaign_failures} fields={["event_type", "executed_at", "error_message"]} />
          <FailureList title="email failures" items={failures.email_failures} fields={["subject", "created_at", "error"]} />
          <FailureList title="sms failures" items={failures.sms_failures} fields={["phone_number", "created_at", "error_message"]} />
        </section>
      )}

      {tab === "audit" && (
        <section className="mt-6 border p-4" style={{ borderColor: "var(--border)" }} data-testid="audit-tab">
          <div className="overline mb-3">// recent actions</div>
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {audit.map((e) => (
              <div key={e.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`audit-row-${e.id}`}>
                <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>{e.action}</span>
                <span className="flex-1 truncate" style={{ color: "var(--text-2)" }}>
                  {e.entity_type ? `${e.entity_type}:${(e.entity_id || "").slice(0, 8)} ` : ""}
                  {e.user_id ? `by user ${e.user_id.slice(0, 8)}` : ""}
                </span>
                <span className="overline">{new Date(e.created_at).toLocaleString()}</span>
              </div>
            ))}
            {audit.length === 0 && (
              <div className="text-sm" style={{ color: "var(--text-3)" }}>No audit events yet.</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function Card({ icon, label, value, sub, testid, danger }) {
  return (
    <div
      className="border p-4"
      style={{ borderColor: danger ? "#FF3B30" : "var(--border)", background: danger ? "rgba(255,59,48,0.05)" : "transparent" }}
      data-testid={testid}
    >
      <div className="overline flex items-center gap-2" style={{ color: danger ? "#FF3B30" : "var(--text-3)" }}>{icon} {label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: danger ? "#FF3B30" : "var(--text-1)" }}>{value}</div>
      {sub && <div className="overline mt-1">{sub}</div>}
    </div>
  );
}

function FailureList({ title, items, fields }) {
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }}>
      <div className="overline mb-3">// {title} · {items?.length || 0}</div>
      {!items || items.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--text-3)" }}>Nothing failing — ship-shape.</div>
      ) : (
        <div className="divide-y text-sm" style={{ borderColor: "var(--border)" }}>
          {items.slice(0, 25).map((it, i) => (
            <div key={i} className="py-2 flex gap-3">
              {fields.map((f) => (
                <span key={f} className="min-w-0 truncate" style={{ color: f === "error" || f === "error_message" ? "#FF3B30" : "var(--text-2)" }}>
                  {String(it[f] || "").slice(0, 80)}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
